import {
  InfiniteTable,
  InfiniteTableColumns,
  InfiniteTableSortBy,
  Link,
  Pill,
  PillKind,
  Stack,
} from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { StatusRevertedTooltip } from './company_onboarding/status_reverted_tooltip';
import { Tags } from '../../components/tags';
import { CompanyIdentifier, OnboardingActionKey, UnifiedCompanySignupStatus } from '../../lib/graphql';
import { customSlugify, shipmentsViewUrl } from '../../lib/util/url';
import { Routes } from '../../routes';
import { Company } from '../entities';
import {
  mapIdentifierType,
  getSignupStatusName,
  getOnboardingStatusPillKind,
  getAgreementDpaPill,
  getAgreementSortValue,
} from '../utils';

const PLATFORM_PILL_KINDS: PillKind[] = ['info', 'success', 'warning', 'danger', 'neutral'];

interface CompanyPlatforms {
  platforms: { platform_id: string; platform_name: string; company_id_on_platform: string }[];
}

export interface CompanyListDataProps {
  companies: (Company & CompanyPlatforms)[];
}

interface CompanyRow {
  id: string;
  company_id: string;
  company_name: string | null;
  is_shipper: boolean | null;
  is_carrier: boolean | null;
  is_pilot: boolean | null;
  is_paying_customer: boolean | null;
  is_test: boolean | null;
  isPendingSignup: boolean | null;
  unifiedSignupStatus: UnifiedCompanySignupStatus | null | undefined;
  signupStatusLabel: string;
  signupStatusTransitionedAt: string | null | undefined;
  onboardingActionType: OnboardingActionKey | null | undefined;
  agreement: { hasAgreement: boolean; agreementVersion: number | null } | null;
  typeSort: string;
  shipperToursCount: number;
  carrierToursCount: number;
  totalToursCount: number;
  totalVehiclesCount: number;
  tags: { name: string }[];
  platforms: CompanyPlatforms['platforms'];
  companyIdentifiers: Pick<CompanyIdentifier, 'identifierType' | 'identifierValue' | 'companyIdentifierId'>[];
}

const getSortValue = (row: CompanyRow, key: keyof CompanyRow): string | number => {
  switch (key) {
    case 'company_id':
      return parseInt(row.company_id, 10);
    case 'company_name':
      return row.company_name ?? '';
    case 'typeSort':
      return row.typeSort;
    case 'signupStatusLabel':
      return row.signupStatusLabel;
    case 'agreement':
      return getAgreementSortValue(row.agreement);
    case 'totalToursCount':
      return row.totalToursCount;
    case 'totalVehiclesCount':
      return row.totalVehiclesCount;
    default:
      return '';
  }
};

const sortData = (data: CompanyRow[], sortBy: InfiniteTableSortBy<keyof CompanyRow>): CompanyRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const CompanyList: React.FC<CompanyListDataProps> = ({ companies }) => {
  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof CompanyRow>>({
    columnId: 'company_id',
    order: 'ASC',
  });

  const tableData: CompanyRow[] = useMemo(
    () =>
      sortData(
        companies.map((company) => ({
          id: company.company_id,
          company_id: company.company_id,
          company_name: company.company_name,
          is_shipper: company.is_shipper,
          is_carrier: company.is_carrier,
          is_pilot: company.is_pilot,
          is_paying_customer: company.is_paying_customer,
          is_test: company.is_test,
          isPendingSignup: company.isPendingSignup,
          unifiedSignupStatus: company.unifiedSignupStatus,
          signupStatusLabel: notNil(company.unifiedSignupStatus)
            ? getSignupStatusName(company.unifiedSignupStatus)
            : '',
          signupStatusTransitionedAt: company.signupStatusTransitionedAt,
          onboardingActionType: company.onboardingAction?.type,
          agreement: company.agreement ?? null,
          typeSort: [
            company.isPendingSignup === true ? 'Pending signup' : undefined,
            company.is_carrier === true ? 'Carrier' : undefined,
            company.is_shipper === true ? 'Shipper' : undefined,
            company.is_pilot === true ? 'Pilot' : undefined,
            company.is_paying_customer === true ? 'Paying' : undefined,
            company.is_test === true ? 'Test' : undefined,
          ]
            .filter(notNil)
            .join(';'),
          shipperToursCount: company.shipperToursCount ?? 0,
          carrierToursCount: company.carrierToursCount ?? 0,
          totalToursCount: (company.shipperToursCount ?? 0) + (company.carrierToursCount ?? 0),
          totalVehiclesCount: company.totalVehiclesCount ?? 0,
          tags: company.tags,
          platforms: company.platforms,
          companyIdentifiers: company.companyIdentifiers ?? [],
        })),
        sortBy,
      ),
    [companies, sortBy],
  );

  const columns: InfiniteTableColumns<CompanyRow> = useMemo(
    () => [
      {
        id: 'company_id',
        title: '#',
        width: '4%',
        isUserSortable: true,
        align: 'end' as const,
        cellContent: ({ row }) => <>{row.company_id}</>,
      },
      {
        id: 'company_name',
        title: 'Name',
        width: '17%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink
            to={Routes.Company.generatePath({
              company_id: row.company_id,
              slug_company_name: customSlugify(row.company_name),
            })}>
            {row.company_name}
          </NavLink>
        ),
      },
      {
        id: 'typeSort',
        title: 'Type',
        width: '13%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {row.isPendingSignup === true && <Pill kind="danger">Pending signup</Pill>}
            {row.is_carrier === true && <Pill kind="info">Carrier</Pill>}
            {row.is_shipper === true && <Pill kind="success">Shipper</Pill>}
            {row.is_pilot === true && <Pill kind="neutral">Pilot</Pill>}
            {row.is_paying_customer === true && <Pill kind="success">Paying</Pill>}
            {row.is_test === true && <Pill kind="warning">Test</Pill>}
          </span>
        ),
      },
      {
        id: 'signupStatusLabel',
        title: 'Onboarding',
        width: '13%',
        isUserSortable: true,
        cellContent: ({ row }) =>
          notNil(row.unifiedSignupStatus) ? (
            <Stack direction="row" alignItems="center" gap="small">
              <Pill kind={getOnboardingStatusPillKind(row.unifiedSignupStatus)}>{row.signupStatusLabel}</Pill>
              {row.onboardingActionType === OnboardingActionKey.ONBOARDING_ROLLED_BACK && (
                <StatusRevertedTooltip
                  statusLabel={row.signupStatusLabel}
                  transitionedAt={row.signupStatusTransitionedAt}
                />
              )}
            </Stack>
          ) : (
            <>-</>
          ),
      },
      {
        id: 'agreement',
        title: 'Agreement / DPA',
        width: '8%',
        isUserSortable: true,
        cellContent: ({ row }) => {
          const pill = getAgreementDpaPill(row.agreement);
          return <Pill kind={pill.kind}>{pill.label}</Pill>;
        },
      },
      {
        id: 'totalToursCount',
        title: 'Tours (shipper/carrier)',
        width: '10%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <>
            <NavLink to={`${Routes.Tours.generatePath({})}?shipper_id=${row.company_id}`}>
              {row.shipperToursCount}
            </NavLink>
            {' / '}
            <NavLink to={`${Routes.Tours.generatePath({})}?carrier_id=${row.company_id}`}>
              {row.carrierToursCount}
            </NavLink>
          </>
        ),
      },
      {
        id: 'totalVehiclesCount',
        title: 'Vehicles',
        width: '5%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={`${Routes.Vehicles.generatePath({})}?company_id=${row.company_id}`}>
            {row.totalVehiclesCount}
          </NavLink>
        ),
      },
      {
        id: 'tags',
        title: 'Tags',
        width: '7%',
        cellContent: ({ row }) => <Tags tags={row.tags} />,
      },
      {
        id: 'platforms',
        title: 'Platforms',
        width: '12%',
        cellContent: ({ row }) => (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {row.platforms.map((platform) => (
              <Pill
                key={platform.platform_id}
                kind={PLATFORM_PILL_KINDS[parseInt(platform.platform_id, 10) % PLATFORM_PILL_KINDS.length]}>
                {`${platform.platform_name}#${platform.company_id_on_platform}`}
              </Pill>
            ))}
          </span>
        ),
      },
      {
        id: 'companyIdentifiers',
        title: 'Identifiers',
        width: '8%',
        cellContent: ({ row }) => <CompanyIdentifierLabels companyIdentifiers={row.companyIdentifiers} />,
      },
      {
        id: 'isPendingSignup',
        title: '',
        width: '3%',
        cellContent: ({ row }) => (
          <a target="_blank" href={shipmentsViewUrl(row.company_id)} title="Open Shipments UI" rel="noreferrer">
            📦
          </a>
        ),
      },
    ],
    [],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  return (
    <div ref={containerRefCallback}>
      {scrollParent !== null && (
        <InfiniteTable<CompanyRow>
          columns={columns}
          data={tableData}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          scrollParent={scrollParent}
          className="compact-table"
        />
      )}
    </div>
  );
};

type CompanyIdentifierLabelsProps = {
  companyIdentifiers: Pick<CompanyIdentifier, 'identifierType' | 'identifierValue' | 'companyIdentifierId'>[];
};

const CompanyIdentifierLabels = ({ companyIdentifiers }: CompanyIdentifierLabelsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMoreThan2 = companyIdentifiers.length > 2;

  const visibleIdentifiers = isExpanded ? companyIdentifiers : companyIdentifiers.slice(0, 2);

  return (
    <>
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 11 }}>
        {visibleIdentifiers.map(({ identifierType, identifierValue, companyIdentifierId }) => (
          <Pill kind="neutral" key={companyIdentifierId}>
            {mapIdentifierType(identifierType)}: {identifierValue}
          </Pill>
        ))}
      </span>
      {hasMoreThan2 && (
        <>
          <br />
          <Link as="button" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? 'Show less' : 'Show more'}
          </Link>
        </>
      )}
    </>
  );
};
